import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import { CSVLink } from "react-csv";
import { Button, Container } from "@material-ui/core";
import { useParams } from "react-router-dom";
import Table from "./table";
import html2canvas from "html2canvas";
import P1 from "assets/p1";
import P2 from "assets/p2";
import { data } from "jquery";

const ExportReactCSV = ({ data, headers, fileName }) => {
  const noDotFileName = () => {
    let updatedFileName = fileName;
    if (updatedFileName.includes(".")) {
      // Remove all dots from the filename
      updatedFileName = updatedFileName.replace(/\./g, "");
    }
    return updatedFileName;
  };

  return (
    <Button variant="contained" style={{ color: "white" }}>
      <CSVLink data={data} headers={headers} filename={noDotFileName()}>
        Export All Data (CSV file)
      </CSVLink>
    </Button>
  );
};

const Database = (props) => {
  const [assigns, setAssigns] = useState(null);
  const [cleanData, setCleanData] = useState(null);
  const [tableData, setTableData] = useState(null);
  const [header, setHeader] = useState(null);
  const [allComponents, setAllComponents] = useState(null);
  const { app } = useParams();
  const [studyId, setStudyId] = useState(null);
  const participantId = useRef(null);
  const experimentId = useRef(null);
  const localStudyId = useRef(null);

  useEffect(() => {
    (async () => {
      const doc = await axios.get(`/api/gui/assign/${app}`);
      setAssigns(doc.data);
    })();
  }, []);

  const findStudyId = (data) => {
    for (let item of data) {
      const studyId = item.assigns.find((assign) => assign.type === "studyids");
      if (studyId) {
        return studyId.id;
      }
    }
    return null;
  };

  const findParticipantId = (data) => {
    data.assigns.map((assign) => {
      if (assign.type === "studyids") {
        participantId.current = assign.participantId;
      }
    });
  };

  const findExperimentId = (data) => {
    data.assigns.map((assign) => {
      if (assign.type === "studyids") {
        experimentId.current = assign.experimentId;
      }
    });
  };

  useEffect(() => {
    localStudyId.current = studyId;
  }, [studyId]);

  useEffect(async () => {
    if (!assigns) return;

    const appDoc = await axios.get(`/api/gui/app/${app}`);
    const studyidsId = findStudyId(assigns);
    setStudyId(
      studyidsId
        ? (await axios.get(`/api/gui/studyids/${studyidsId}`)).data.studyId
        : appDoc.data.id
    );

    const componentMap = new Map();
    for (const assignData of assigns) {
      for (const assign of assignData.assigns) {
        const identifier = assign.question || assign.title || assign.type;
        if (!componentMap.has(identifier)) {
          componentMap.set(identifier, assign.type);
        }
      }
    }

    const sortedIdentifiers = Array.from(componentMap.keys()).sort();
    const identifierToIndex = new Map(
      sortedIdentifiers.map((id, index) => [id, index])
    );

    const maxLengths = {};
    for (const assignData of assigns) {
      for (const assign of assignData.assigns) {
        const identifier = assign.question || assign.title || assign.type;
        const index = identifierToIndex.get(identifier);
        const key = `${index}-${assign.type}`;

        let len = 0;
        if (assign.type === "recoverystimulus")
          len = assign.answer?.sentenceAnswers?.length || 0;
        else if (assign.type === "digits" || assign.type === "crm")
          len = assign.answer?.answers?.length || 0;
        else if (assign.type === "alphabetexperiment")
          len = assign.answer?.length || 0;

        if (len > (maxLengths[key] || 0)) maxLengths[key] = len;
      }
    }

    const masterHeaders = [
      "study-name",
      "study-id",
      "experiment-id",
      "participant-id",
      "start-time",
      "end-time",
    ];
    const headerSet = new Set(masterHeaders);

    for (const identifier of sortedIdentifiers) {
      const index = identifierToIndex.get(identifier);
      const type = componentMap.get(identifier);
      const addHeader = (h) => {
        if (!headerSet.has(h)) {
          headerSet.add(h);
          masterHeaders.push(h);
        }
      };

      switch (type) {
        case "radio":
          addHeader(`${index}-radio-question`);
          addHeader(`${index}-radio-answer`);
          break;
        case "select":
        case "ageselect":
          addHeader(`${index}-select-question`);
          addHeader(`${index}-select-answer`);
          break;
        case "textfield":
          addHeader(`${index}-textfield-question`);
          addHeader(`${index}-textfield-answer`);
          break;
        case "datepicker":
          addHeader(`${index}-date-question`);
          addHeader(`${index}-date-answer`);
          break;
        case "imagerandom":
          addHeader(`${index}-random-image`);
          break;
        case "textrandom":
          addHeader(`${index}-random-text-section`);
          break;
        case "slider":
          addHeader(`${index}-slider-question`);
          addHeader(`${index}-slider-answer`);
          break;
        case "sliderv2":
          addHeader(`${index}-sliderv2-question`);
          addHeader(`${index}-sliderv2-answer`);
          addHeader(`${index}-sliderv2-maxValue`);
          break;
        case "ohhis":
          addHeader(`${index}-OHHIS`);
          break;
        case "checkbox":
          addHeader(`${index}-checkbox-question`);
          addHeader(`${index}-checkbox-choices`);
          addHeader(`${index}-checkbox-answer`);
          break;
        case "randomid":
          addHeader(`${index}-random-id`);
          break;
        case "volumecontrol":
          addHeader(`${index}-volumecontrol-title`);
          addHeader(`${index}-volumecontrol-answer`);
          break;
        case "dfdexperiments":
          addHeader(`${index}-Question`);
          addHeader(`${index}-Answer`);
          addHeader(`${index}-decibel`);
          addHeader(`${index}-reactTimer`);
          break;
        case "recoverystimulus":
          addHeader(`${index}-DIN-SNR`);
          for (let j = 1; j <= (maxLengths[`${index}-${type}`] || 0); j++) {
            addHeader(`${index}-${j}-recovery-name`);
            addHeader(`${index}-${j}-recovery-answer`);
            addHeader(`${index}-${j}-DIN-question`);
            addHeader(`${index}-${j}-DIN-answer`);
            addHeader(`${index}-${j}-audio-db`);
            addHeader(`${index}-${j}-recovery-audio-start`);
            addHeader(`${index}-${j}-recovery-target-start`);
            addHeader(`${index}-${j}-recovery-target-end`);
            addHeader(`${index}-${j}-recovery-response-start`);
            addHeader(`${index}-${j}-recovery-response-end`);
            addHeader(`${index}-${j}-DIN-audio-start`);
            addHeader(`${index}-${j}-DIN-first-digit`);
            addHeader(`${index}-${j}-DIN-second-digit`);
            addHeader(`${index}-${j}-DIN-third-digit`);
            addHeader(`${index}-${j}-DIN-response-end`);
          }
          break;
        case "digits":
        case "crm":
          const prefix = type === "digits" ? "DIN" : "CRM";
          addHeader(`${index}-${prefix}-SNR`);
          if (type === "crm") addHeader(`${index}-CRM-type`);
          for (let j = 1; j <= (maxLengths[`${index}-${type}`] || 0); j++) {
            if (type === "digits") {
              addHeader(`${index}-${j}-DIN-reacttime`);
              addHeader(`${index}-${j}-DIN-realtime`);
            } else {
              // crm
              addHeader(`${index}-${j}-DIN-timer`);
              addHeader(`${index}-${j}-DIN-correct`);
            }
            addHeader(`${index}-${j}-DIN-question`);
            addHeader(`${index}-${j}-DIN-answer`);
            addHeader(`${index}-${j}-DIN-db`);
          }
          break;
        case "alphabetexperiment":
          addHeader(`${index}-Question`);
          addHeader(`${index}-Answer`);
          addHeader(`${index}-Score`);
          addHeader(`${index}-similarity`);
          for (let j = 0; j < (maxLengths[`${index}-${type}`] || 0); j++) {
            addHeader(`${index}-${j}-Answer`);
            addHeader(`${index}-${j}-Question`);
            addHeader(`${index}-${j}-letterTimings letter`);
            addHeader(`${index}-${j}-letterTimings appearTime`);
            addHeader(`${index}-${j}-letterTimings disappearTime`);
            addHeader(`${index}-${j}-similarity`);
          }
          break;
      }
    }

    const allData = [];
    for (const assignData of assigns) {
      findParticipantId(assignData);
      findExperimentId(assignData);

      const rowMap = new Map([
        ["study-name", appDoc.data.name],
        ["study-id", localStudyId.current],
        ["experiment-id", experimentId.current],
        ["participant-id", participantId.current || assignData.id],
        [
          "start-time",
          assignData.startAt
            ? new Date(assignData.startAt).toLocaleString("en-US", {
                timeZone: "America/Denver",
              })
            : "Not recorded",
        ],
        [
          "end-time",
          assignData.endAt
            ? new Date(assignData.endAt).toLocaleString("en-US", {
                timeZone: "America/Denver",
              })
            : "Not recorded",
        ],
      ]);

      for (const assign of assignData.assigns) {
        const identifier = assign.question || assign.title || assign.type;
        const index = identifierToIndex.get(identifier);
        const ZWSP = String.fromCharCode(8203); // Zero-width space

        switch (assign.type) {
          case "radio":
            rowMap.set(`${index}-radio-question`, assign.question);
            rowMap.set(`${index}-radio-answer`, assign.answer);
            break;
          case "select":
          case "ageselect":
            rowMap.set(`${index}-select-question`, assign.question);
            rowMap.set(`${index}-select-answer`, assign.answer);
            break;
          case "textfield":
            rowMap.set(`${index}-textfield-question`, assign.question);
            rowMap.set(`${index}-textfield-answer`, assign.answer);
            break;
          case "datepicker":
            rowMap.set(`${index}-date-question`, assign.question);
            rowMap.set(`${index}-date-answer`, assign.answer);
            break;
          case "imagerandom":
            rowMap.set(`${index}-random-image`, assign.image);
            break;
          case "textrandom":
            rowMap.set(`${index}-random-text-section`, assign.text);
            break;
          case "slider":
            rowMap.set(`${index}-slider-question`, assign.question);
            rowMap.set(`${index}-slider-answer`, assign.answer);
            break;
          case "sliderv2":
            rowMap.set(`${index}-sliderv2-question`, assign.question);
            rowMap.set(`${index}-sliderv2-answer`, assign.answer);
            rowMap.set(`${index}-sliderv2-maxValue`, assign.maxValue);
            break;
          case "ohhis":
            rowMap.set(`${index}-OHHIS`, assign.code);
            break;
          case "checkbox":
            rowMap.set(`${index}-checkbox-question`, assign.question);
            rowMap.set(`${index}-checkbox-choices`, String(assign.options));
            rowMap.set(`${index}-checkbox-answer`, String(assign.answer));
            break;
          case "randomid":
            rowMap.set(`${index}-random-id`, assign.answer + ZWSP);
            break;
          case "volumecontrol":
            rowMap.set(`${index}-volumecontrol-title`, assign.title);
            rowMap.set(`${index}-volumecontrol-answer`, assign.answer + ZWSP);
            break;
          case "dfdexperiments":
            rowMap.set(`${index}-Question`, assign.answer.question);
            rowMap.set(`${index}-Answer`, assign.answer.answer);
            rowMap.set(`${index}-decibel`, assign.answer.decibel);
            rowMap.set(`${index}-reactTimer`, assign.answer.reactTimer);
            break;
          case "recoverystimulus":
            rowMap.set(`${index}-DIN-SNR`, assign.answer.SNR + ZWSP);
            assign.answer.sentenceAnswers.forEach((sa, j) => {
              rowMap.set(`${index}-${j + 1}-recovery-name`, sa.name);
              rowMap.set(`${index}-${j + 1}-recovery-answer`, sa.answer);
              rowMap.set(
                `${index}-${j + 1}-DIN-question`,
                assign.answer.questions[j] + ZWSP
              );
              rowMap.set(
                `${index}-${j + 1}-DIN-answer`,
                assign.answer.answers[j] + ZWSP
              );
              rowMap.set(
                `${index}-${j + 1}-audio-db`,
                assign.answer.dbs[j + 1] + ZWSP
              );
              const ts = assign.answer.timestamps[j];
              if (ts) {
                rowMap.set(
                  `${index}-${j + 1}-recovery-audio-start`,
                  ts[0] + ZWSP
                );
                rowMap.set(
                  `${index}-${j + 1}-recovery-target-start`,
                  ts[1] + ZWSP
                );
                rowMap.set(
                  `${index}-${j + 1}-recovery-target-end`,
                  ts[2] + ZWSP
                );
                rowMap.set(
                  `${index}-${j + 1}-recovery-response-start`,
                  ts[3] + ZWSP
                );
                rowMap.set(
                  `${index}-${j + 1}-recovery-response-end`,
                  ts[4] + ZWSP
                );
                rowMap.set(`${index}-${j + 1}-DIN-audio-start`, ts[5] + ZWSP);
                rowMap.set(`${index}-${j + 1}-DIN-first-digit`, ts[6] + ZWSP);
                rowMap.set(`${index}-${j + 1}-DIN-second-digit`, ts[7] + ZWSP);
                rowMap.set(`${index}-${j + 1}-DIN-third-digit`, ts[8] + ZWSP);
                rowMap.set(`${index}-${j + 1}-DIN-response-end`, ts[9] + ZWSP);
              }
            });
            break;
          case "digits":
            rowMap.set(`${index}-DIN-SNR`, assign.answer.SNR + ZWSP);
            assign.answer.answers.forEach((ans, j) => {
              rowMap.set(
                `${index}-${j + 1}-DIN-reacttime`,
                assign.answer.reactTimer[j] + ZWSP
              );
              rowMap.set(
                `${index}-${j + 1}-DIN-realtime`,
                assign.answer.realTimer[j] + ZWSP
              );
              rowMap.set(
                `${index}-${j + 1}-DIN-question`,
                assign.answer.questions[j] + ZWSP
              );
              rowMap.set(`${index}-${j + 1}-DIN-answer`, ans + ZWSP);
              rowMap.set(
                `${index}-${j + 1}-DIN-db`,
                assign.answer.dbs[j + 1] + ZWSP
              );
            });
            break;
          case "crm":
            rowMap.set(`${index}-CRM-type`, assign.answer.type);
            rowMap.set(`${index}-CRM-SNR`, assign.answer.SNR + ZWSP);
            assign.answer.answers.forEach((ans, j) => {
              rowMap.set(
                `${index}-${j + 1}-DIN-timer`,
                assign.answer.timer[j] + ZWSP
              );
              rowMap.set(
                `${index}-${j + 1}-DIN-question`,
                assign.answer.questions[j] + ZWSP
              );
              rowMap.set(`${index}-${j + 1}-DIN-answer`, ans + ZWSP);
              rowMap.set(
                `${index}-${j + 1}-DIN-correct`,
                assign.answer.correct[j] + ZWSP
              );
              rowMap.set(
                `${index}-${j + 1}-DIN-db`,
                assign.answer.dbs[j + 1] + ZWSP
              );
            });
            break;
          case "alphabetexperiment":
            rowMap.set(`${index}-Question`, assign.question);
            rowMap.set(`${index}-Answer`, assign.answer);
            rowMap.set(`${index}-Score`, assign.score);
            rowMap.set(`${index}-similarity`, assign.similarity?.current);
            assign.answer.forEach((ans, j) => {
              rowMap.set(`${index}-${j}-Answer`, ans);
              rowMap.set(`${index}-${j}-Question`, assign.question[j]);
              rowMap.set(
                `${index}-${j}-letterTimings letter`,
                assign.letterTimings[j]?.letter
              );
              rowMap.set(
                `${index}-${j}-letterTimings appearTime`,
                assign.letterTimings[j]?.appearTime
              );
              rowMap.set(
                `${index}-${j}-letterTimings disappearTime`,
                assign.letterTimings[j]?.disappearTime
              );
              rowMap.set(
                `${index}-${j}-similarity`,
                assign.similarity.current[j]
              );
            });
            break;
        }
      }
      allData.push(masterHeaders.map((h) => rowMap.get(h) || ""));
    }

    setCleanData(allData);
    setHeader(masterHeaders);

    const tableData = assigns.map((assignData) => {
      findParticipantId(assignData);
      findExperimentId(assignData);
      return {
        experimentId: experimentId.current,
        participantId: participantId.current,
        id: assignData.id,
        endAt: assignData.endAt,
      };
    });

    setTableData(tableData);
  }, [assigns]);

  const handleDeleteData = async (id) => {
    await axios.delete("/api/gui/assign/" + id);
    window.location.reload();
  };

  const renderConfusionMatrixTable = (confusionMatrix, id) => {
    const buttonLabels = [
      "aBil",
      "aCHil",
      "aDil",
      "aFil",
      "aGil",
      "aHil",
      "aJil",
      "aKil",
      "aLil",
      "aMil",
      "aNil",
      "aPil",
      "aRil",
      "aSHil",
      "aSil",
      "aTHil",
      "aTil",
      "aVil",
      "aWil",
      "aYil",
      "aZil",
    ];

    if (!confusionMatrix) {
      return <p>No confusion matrix data available.</p>;
    }

    // Convert the object-based confusionMatrix to a two-dimensional array
    const matrixData = buttonLabels.map((label) => confusionMatrix[label]);

    return (
      <table id={id} style={styles.confusionMatrixTable}>
        <thead>
          <tr>
            <th style={styles.confusionMatrixHeader}></th>
            {buttonLabels.map((label, i) => (
              <th key={`header-${i}`} style={styles.confusionMatrixHeader}>
                {label}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {buttonLabels.map((label, i) => (
            <tr
              key={`row-${i}`}
              style={
                i % 2 === 0
                  ? styles.confusionMatrixEvenRow
                  : styles.confusionMatrixOddRow
              }
            >
              <td style={styles.confusionMatrixCell}>{label}</td>
              {matrixData[i].map((value, j) => (
                <td key={`cell-${i}-${j}`} style={styles.confusionMatrixCell}>
                  {value}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    );
  };

  const saveConfusionMatrixImage = (tableId, componentId) => {
    const table = document.getElementById(tableId); // Add an ID to the table element
    html2canvas(table).then(function (canvas) {
      const link = document.createElement("a");
      link.href = canvas.toDataURL("image/png");
      link.download = `confusion_matrix_${componentId}.png`; // Use the componentId as the filename
      link.click();
    });
  };

  const handleDFD = (assigns) => {
    if (!assigns) {
      return null; // Handle the case when `assigns` is undefined
    }

    const dfdExperiments = [];

    // Loop over assigns and sort components
    assigns.forEach((assignData) => {
      assignData.assigns.forEach((assignComponent) => {
        if (assignComponent.type === "dfdexperiments") {
          dfdExperiments.push({
            id: assignData.id,
            component: assignComponent,
          });
        } else {
          return null;
        }
      });
    });

    const confusionMatrixElements = [];

    dfdExperiments.forEach((dfdExperiment) => {
      const { component: sortedComponent, id: componentId } = dfdExperiment;
      const confusionMatrix = sortedComponent.confusionMatrix;
      const tableId = `confusion-matrix-${componentId}`; // Create a unique ID for the table element

      const accordionStyle = {
        border: "1px solid #ddd", // Add a light grey border
        borderRadius: "5px", // Rounded corners for the border
        marginBottom: "20px", // Space between each accordion item
      };

      const detailsStyle = {
        padding: "10px", // Padding inside the accordion for content
      };

      const tableStyle = {
        width: "100%", // Ensure the table uses the full width
        marginBottom: "20px", // Space below the table
        borderCollapse: "collapse", // Collapse borders between table cells
      };

      const thTdStyle = {
        border: "1px solid #ddd", // Add a light grey border around each cell
        padding: "8px", // Padding inside each cell
        textAlign: "left", // Align text to the left
      };

      const renderStyledConfusionMatrixTable = (confusionMatrix, tableId) => {
        // Assume this function renders the table with the provided styles
        // You would need to implement the styling within this function,
        // applying the tableStyle and thTdStyle to the table, th, and td elements respectively.
        return renderConfusionMatrixTable(confusionMatrix, tableId); // Placeholder
      };

      const element = (
        <div
          key={sortedComponent.id}
          style={{ ...accordionStyle, marginTop: "20px" }}
        >
          <details>
            <summary>
              <h5 style={{ marginLeft: "25px" }}>
                Confusion Matrix {componentId}
              </h5>
            </summary>
            <div style={detailsStyle}>
              {renderStyledConfusionMatrixTable(confusionMatrix, tableId)}
              <button
                style={styles.button}
                onClick={() => saveConfusionMatrixImage(tableId, componentId)}
              >
                Download Confusion Matrix
              </button>
            </div>
          </details>
        </div>
      );

      confusionMatrixElements.push(element);
    });

    return confusionMatrixElements;
  };

  const handleSubsequentAudio = (assigns) => {
    const subsequentErrors = [];

    // Loop over assigns and sort components
    assigns?.forEach((assignData) => {
      assignData.assigns.forEach((assignComponent) => {
        if (assignComponent.type === "recoverystimulus") {
          subsequentErrors.push({
            id: assignData.id,
            block: assignComponent?.block,
            trial: assignComponent?.trial,
          });
        } else {
          return null;
        }
      });
    });

    const handleDownloadAll = async (rid) => {
      try {
        // Construct the URL for the zip download endpoint
        const zipUrl = `/api/gui/recoverystimulus/downloadAll/${rid}`;

        // Trigger the zip file download
        window.location.href = zipUrl;
      } catch (error) {
        console.error("Error when trying to download the zip file:", error);
      }
    };

    return (
      <div>
        {subsequentErrors.map((subsequentError) => {
          const { id, block, trial } = subsequentError;
          return (
            <div key={id}>
              <h3>Subsequent Error Audios </h3>
              <p>
                <strong>Assign ID:</strong> {id}
              </p>
              <p>
                <strong>Block:</strong> {block}
              </p>
              <p>
                <strong>Trial:</strong> {trial}
              </p>
              {/* <button onClick={() => handleDownload(id, block, trial)}>
                Download Audio
              </button> */}
              <button onClick={() => handleDownloadAll(id)}>
                Download All Audios
              </button>
            </div>
          );
        })}
      </div>
    );
  };

  return (
    <Container style={{ marginBottom: "50px", paddingTop: "5vh" }}>
      {tableData && (
        <Table
          style={{ marginTop: "20px" }}
          app={app}
          StudyId={studyId}
          data={tableData}
          deleteData={handleDeleteData}
          handleSubsequentAudio={handleSubsequentAudio}
        />
      )}
      {cleanData && header ? (
        <ExportReactCSV
          data={cleanData}
          headers={header}
          fileName={props.match.params.app}
        />
      ) : null}
      <div>{handleSubsequentAudio(assigns)}</div>
      <div>{assigns && handleDFD(assigns)}</div>
    </Container>
  );
};

const styles = {
  table: {
    borderCollapse: "collapse",
    width: "60%",
    margin: "0 auto",
    marginTop: "-5%",
    tableLayout: "fixed", // Add this
  },
  tableData: {
    border: "1px solid #ccc",
    padding: "8px", // Reduce padding from 8px to 4px
    textAlign: "center",
    verticalAlign: "middle",
    width: "33.33%", // Set width to 33.33%
  },
  button: {
    backgroundColor: "#007a33", // UofA green
    border: "none",
    color: "white",
    padding: "10px 24px", // Adjust padding to 10px 24px
    textAlign: "center",
    textDecoration: "none",
    fontSize: "30px",
    cursor: "pointer",
    borderRadius: "4px",
    width: "100%", // Set button width to 100%
  },
  redButton: {
    backgroundColor: "#b34f4a",
    border: "none",
    color: "white",
    padding: "10px 24px", // Adjust padding to 10px 24px
    textAlign: "center",
    textDecoration: "none",
    fontSize: "30px",
    cursor: "pointer",
    borderRadius: "4px",
    width: "100%", // Set button width to 100%
  },
  playButton: {
    backgroundColor: "#f2cd00", // UofA green
    border: "none",
    color: "white",
    padding: "10px 20px",
    textAlign: "center",
    textDecoration: "none",
    display: "flex", // Change display to flex
    justifyContent: "center", // Add justifyContent for centering
    alignItems: "center", // Add alignItems for vertical alignment
    fontSize: "25px",
    cursor: "pointer",
    borderRadius: "4px",
    marginLeft: "10px", // Set marginLeft to create space between buttons
    marginRight: "auto",
    marginTop: "20px", // Reduce marginTop value to adjust height
    width: "200px", // Set a fixed width
  },

  confusionMatrixTable: {
    borderCollapse: "collapse",
    marginLeft: "auto",
    marginRight: "auto",
    marginTop: "20px",
    textAlign: "center",
    fontFamily: "Arial, sans-serif",
    width: "80%",
    border: "1px solid #ccc",
  },
  confusionMatrixHeader: {
    backgroundColor: "#f1f1f1",
    color: "#333",
    fontWeight: "bold",
    padding: "10px",
    borderBottom: "1px solid #ccc",
  },
  confusionMatrixEvenRow: {
    backgroundColor: "#ffffff",
  },
  confusionMatrixOddRow: {
    backgroundColor: "#f9f9f9",
  },
  confusionMatrixCell: {
    padding: "10px",
    borderBottom: "1px solid #ccc",
  },
};

export default Database;

//Archive
// const handleDownload = async (id, block, trial) => {
//   try {
//     // Request the signed URL for downloading the audio file
//     const response = await axios.get(
//       `/api/gui/recoverystimulus/downloadAudio/${id}/${block}/2`
//     );
//     const { downloadUrl } = response.data;

//     // Use the signed URL to trigger the file download
//     const link = document.createElement("a");
//     link.href = downloadUrl;
//     link.setAttribute("download", `audio_${id}_${block}_${trial}.wav`);
//     document.body.appendChild(link);
//     link.click();
//     document.body.removeChild(link);
//   } catch (error) {
//     console.error("Error when trying to download the file:", error);
//   }
// };
